import { ChangeDetectorRef, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { AbstractControl, FormBuilder, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Subject, takeUntil } from 'rxjs';
import { FromDictionaryPipe, LanguageService } from '@teamfoster/sdk/dictionary-ngrx';
import { SetTitle } from '../../../store';
import { DynamicFormField } from '@teamfoster/dynamic-forms';
import { Breadcrumb } from 'src/app/core/models/breadcrumb.model';

import * as fromShopify from '../../../shop/store';
import { CreateAccessTokenData } from 'src/app/shop';
import { EmailExistsAsyncValidator } from '../../directives/email-exists.directive';
import { AuthService } from '../../services/auth.service';
import { Router, RouterLink } from '@angular/router';
import { AuthLayoutComponent } from '../../components/auth-layout/auth-layout.component';
import { BreadcrumbsComponent } from '../../../core/components/breadcrumbs/breadcrumbs.component';
import { AlertComponent } from '../../../core/components/alert/alert.component';
import { MatFormField, MatLabel, MatInput, MatSuffix, MatError } from '@angular/material/input';
import { IconComponent } from '../../../core/components/icon/icon.component';
import { AsyncPipe } from '@angular/common';

@Component({
  selector: 'app-sign-in',
  templateUrl: './sign-in.component.html',
  imports: [
    AuthLayoutComponent,
    BreadcrumbsComponent,
    AlertComponent,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    IconComponent,
    MatSuffix,
    MatError,
    RouterLink,
    AsyncPipe,
    FromDictionaryPipe,
  ],
})
export class SignInComponent implements OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private cd = inject(ChangeDetectorRef);
  private authService = inject(AuthService);
  private router = inject(Router);
  private store = inject(Store);
  private lang = inject(LanguageService);

  signingIn$ = this.store.select(fromShopify.getCustomerSigningIn);
  signedIn$ = this.store.select(fromShopify.getCustomerSignedIn);
  errors$ = this.store.select(fromShopify.getAuthError);

  inviting$ = this.store.select(fromShopify.getInviteSending);
  inviteSent$ = this.store.select(fromShopify.getInviteSent);
  recoverErrors$ = this.store.select(fromShopify.getCustomerRecoveredErrors);

  prefix = 'inloggen';

  formFields: DynamicFormField[] = [];

  private dict = new FromDictionaryPipe();

  breadcrumbs: Breadcrumb[] = [];

  group = this.fb.group({
    email: [null, [Validators.required, Validators.email], [EmailExistsAsyncValidator.createValidator(this.authService)]],
    password: [null, [passwordConditionallyRequiredValidator]],
  });

  formSubmitted = false;
  isNewUser = false;

  _unsubscribe$ = new Subject<void>();

  constructor() {}

  ngOnInit(): void {
    this.breadcrumbs = [
      {
        label: this.dict.transform(`account-titel`),
        routerLink: ['/auth'],
      },
      {
        label: this.dict.transform(`inloggen-titel`),
        routerLink: ['/auth/inloggen'],
      },
    ];
    this.formFields = [
      {
        inputType: 'email',
        label: this.dict.transform(`formulier-veld-label-email`),
        name: 'email',
        validators: [Validators.email, Validators.required],
        autocomplete: 'email',
        placeholder: this.dict.transform(`formulier-veld-placeholder-email`, false),
        fieldType: 'input',
        order: 0,
      },
      {
        inputType: 'password',
        label: this.dict.transform(`formulier-veld-label-wachtwoord`),
        name: 'password',
        validators: [Validators.required],
        autocomplete: 'password',
        placeholder: this.dict.transform(`formulier-veld-placeholder-wachtwoord`, false),
        fieldType: 'input',
        order: 1,
      },
    ];

    this.store.dispatch(
      SetTitle({ title: `${this.dict.transform(`${this.prefix}-titel`)} - ${this.dict.transform('meta-titel-suffix')}` })
    );

    this.group.statusChanges.pipe(takeUntil(this._unsubscribe$)).subscribe(a => {
      this.checkExisting();
    });

    this.group
      .get('email')
      ?.valueChanges.pipe(takeUntil(this._unsubscribe$))
      .subscribe(a => {
        this.formSubmitted = false;
        this.cd.detectChanges();
      });
  }

  checkExisting() {
    const field = this.group.get('email');

    if (field && field.errors?.['user_unknown'] === true) {
      this.isNewUser = true;
    } else {
      this.isNewUser = false;
    }

    this.cd.markForCheck();
  }

  handleSubmit(e: any) {
    const data: CreateAccessTokenData = { email: e.email, password: e.password };

    this.checkExisting();
    if (this.isNewUser) {
      this.router.navigate(['/auth/registreren'], { queryParams: { email: this.group.get('email')?.value } });
    }

    if (e.email) {
      if (!this.formSubmitted) {
        this.formSubmitted = true;
        return;
      }
    }

    if (e.password) {
      this.formSubmitted = true;
      this.store.dispatch(fromShopify.CreateCustomerAccessToken({ data }));
    }
  }

  isValid() {
    const field = this.group.get('email');
    return !field?.errors?.['required'] && !field?.errors?.['email'];
  }

  sendActivationMail(email: string) {
    this.store.dispatch(fromShopify.SendInvitation({ email }));
  }

  ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }
}

function passwordConditionallyRequiredValidator(formControl: AbstractControl) {
  if (!formControl.parent) {
    return null;
  }

  if (formControl.parent.get('email')?.valid) {
    return Validators.required(formControl);
  }
  return null;
}
